import { toNumber } from './numbers'

/**
 * Takes an object of simple keys/values and returns a corresponding new object.
 * If there are any keys in the original object whose name end with "_raw" the
 * returned object will have a corresponding key (without the _raw suffix) but
 * with its value always converted into a JS number. If it cannot be converted
 * to a number the value will be zero. This way, you can always do math with the result
 *
 * @param {any} obj
 * @returns {object} Updated object
 */
export const replaceObjectRawKeys = obj => {
  const extraValues = {}
  for (const key in obj) {
    if (key.endsWith('_raw')) {
      extraValues[key.replace('_raw', '')] = toNumber(obj[key])
    }
  }
  return { ...obj, ...extraValues }
}

/**
 * Returns the given item or null if the item is null, undefined, an empty
 * string (optionally after trimming), an empty array, or an empty object.
 *
 * @example
 *   nullIfEmpty(null) // returns null
 *   nullIfEmpty('  ') // returns '  '
 *   nullIfEmpty('  ', true) // returns null
 *   nullIfEmpty([]) // returns null
 *   nullIfEmpty({}) // returns null
 *   nullIfEmpty('Hello') // returns 'Hello'
 *
 * @param {any} item - The item to check.
 * @param {boolean} [trimStrings=false] - If true, will trim the string before
 *   checking if it's empty. Default is `false`
 * @returns {any} Returns the original item or null if it's considered empty
 *   based on the conditions.
 */
export const nullIfEmpty = (item, trimStrings = false) => {
  if (item == null) {
    return null
  }
  if (typeof item === 'string') {
    const val = trimStrings ? item.trim() : item
    return val === '' ? null : item
  }
  if (typeof item === 'object') {
    if (Array.isArray(item) && item.length === 0) {
      return null
    }
    if (Object.keys(item).length === 0) {
      return null
    }
  }
  return item
}

export const removeKeysWithNullishValues = (
  obj,
  includeEmptyString = false
) => {
  const result = {}
  for (const key in obj) {
    const value = obj[key]
    const isNullish = value == null || (includeEmptyString && value === '')
    if (!isNullish) {
      result[key] = value
    }
  }
  return result
}

export const removeKeysWithEmptyValues = obj =>
  removeKeysWithNullishValues(obj, true)

export const shallowEqual = (obj1, obj2) => {
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (let key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false
    }
  }

  return true
}

/**
 * Plucks specified keys from an object.
 *
 * @template T - This represents a generic object type.
 * @param {T} obj - The object to pluck keys from.
 * @param {string[]} keys - The keys to pluck from the object.
 * @returns {Partial<T>} - Returns an object with only the plucked keys.
 */
export const pluck = (obj, keys) => {
  /** @type {Partial<T>} */
  const toReturn = {}
  for (const key of keys) {
    if (obj.hasOwnProperty(key)) {
      toReturn[key] = obj[key]
    }
  }
  return toReturn
}
